// Key of the custom listing field "Disponibilité", configured in Sharetribe Console
// (Build > Content > Listing fields). Not present in local config since it's a hosted asset.
export const AVAILABILITY_FIELD_KEY = 'disponibilite';

const normalize = str =>
  str
    ? str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
    : '';

// The "Disponible aujourd'hui" option is the only one considered "available".
// Matching is done against the Console-configured label text (accents/case-insensitive)
// since the enum option keys themselves aren't mirrored in this codebase.
export const isAvailableNowLabel = label => normalize(label) === normalize("Disponible aujourd'hui");

// MassageDerniereMinute: the "disponible" option value (Console: Build > Content > Listing
// fields > Disponibilité), used instead of the label text above so availability detection
// still works once the label is translated to English on the samedaymassage.com deployment.
const AVAILABLE_OPTION_VALUE = 'disponible';
export const isAvailableNowOption = optionValue => `${optionValue}` === AVAILABLE_OPTION_VALUE;

/**
 * Resolves the availability badge props (label + isAvailable) for a listing,
 * given its publicData and the marketplace configuration.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @param {Object} config marketplace configuration (from useConfiguration())
 * @returns {Object|null} { label, isAvailable } or null if the field isn't set/configured
 */
export const getAvailabilityBadgeProps = (publicData, config) => {
  const fieldConfig = config?.listing?.listingFields?.find(
    field => field.key === AVAILABILITY_FIELD_KEY
  );
  const value = publicData?.[AVAILABILITY_FIELD_KEY];

  if (!fieldConfig || value == null) {
    return null;
  }

  const optionConfig = fieldConfig.enumOptions?.find(option => `${option.option}` === `${value}`);
  const label = optionConfig?.label;

  return label ? { label, isAvailable: isAvailableNowOption(value) } : null;
};

/**
 * Resolves the props needed for a one-click availability toggle: the listing's current
 * status (label + isAvailable), and the enum option value to switch to next.
 *
 * Toggling switches from the "available" option to the first "not available" option, or
 * back to the "Disponible aujourd'hui" option, whichever applies. If the listing doesn't
 * have a value set yet, it's treated as "not available" so the first click sets it to
 * "available".
 *
 * @param {Object} publicData listing.attributes.publicData
 * @param {Object} config marketplace configuration (from useConfiguration())
 * @returns {Object|null} { label, isAvailable, nextValue } or null if the field isn't configured
 */
export const getAvailabilityToggleProps = (publicData, config) => {
  const fieldConfig = config?.listing?.listingFields?.find(
    field => field.key === AVAILABILITY_FIELD_KEY
  );
  const options = fieldConfig?.enumOptions;

  if (!options || options.length === 0) {
    return null;
  }

  const value = publicData?.[AVAILABILITY_FIELD_KEY];
  const currentOption = options.find(option => `${option.option}` === `${value}`);
  const isAvailable = currentOption ? isAvailableNowOption(currentOption.option) : false;
  const notAvailableOption = options.find(option => !isAvailableNowOption(option.option));
  const label = currentOption?.label || notAvailableOption?.label || options[0].label;

  const nextOption = isAvailable
    ? notAvailableOption
    : options.find(option => isAvailableNowOption(option.option));

  return nextOption ? { label, isAvailable, nextValue: nextOption.option } : null;
};
